import { KnBrDraftOrdersService } from 'src/app/store/kn-br-draft-orders/kn-br-draft-orders.service';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  GlobalMessageService,
  GlobalMessageType,
  LoggerService,
  OCC_USER_ID_CURRENT,
  RoutingService,
  tryNormalizeHttpError,
} from '@spartacus/core';
import { noop, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { KnBrCartContextService } from '../kn-br-cart-context/kn-br-cart-context.service';
import {
  KnBrDraftOrderCopyFailure,
  KnBrDraftOrderCopySuccess,
  KnBrDraftOrderCreateFailure,
  KnBrDraftOrderCreateSuccess,
  KnBrDraftOrderLoadStatusesFailure,
  KnBrDraftOrderLoadStatusesSuccess,
  KnBrDraftOrderQuickEntryFailure,
  KnBrDraftOrderQuickEntrySuccess,
  KnBrDraftOrdersActionTypes,
  KnBrDraftOrderSearchFailure,
  KnBrDraftOrderSearchSuccess,
  KnBrDraftOrdersSearchActions,
  KnBrDraftOrderUpdateFailure,
  KnBrDraftOrderUpdateSuccess,
  KnBrDraftOrderPriceAdjustFailure,
  KnBrDraftOrderEntryUpdateFailure,
  KnBrDraftOrderEntryUpdateSuccess,
  KnBrDraftOrderPriceAdjustSuccess,
  KnBrDraftOrderDeleteSuccess,
  KnBrDraftOrderDeleteFailure,
} from './kn-br-draft-orders.action';
import { KnBrDraftOrdersAdapter } from './kn-br-draft-orders.adapter';
import { KnBrQuoteReferenceService } from '../kn-br-quote-reference/kn-br-quote-reference.service';
import * as _ from 'lodash';
import { CartActions, MultiCartService } from '@spartacus/cart/base/core';

@Injectable()
export class KnBrDraftOrdersEffects {
  searchOrders$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderSearchAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.search(action.payload).pipe(
        map((data: any) => {
          return new KnBrDraftOrderSearchSuccess(data);
        }),
        catchError((error: Error) => {
          this.messageService.add({ key: 'knBrMessages.error' }, GlobalMessageType.MSG_TYPE_ERROR);
          return of(new KnBrDraftOrderSearchFailure(error));
        })
      )
    )
  )
);

searchOrdersFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderSearchFailureAction),
    map((action) => {
      this.messageService.add({ key: 'knBrMessages.error' }, GlobalMessageType.MSG_TYPE_ERROR);
    })
  ),
  { dispatch: false }
);

copyOrder$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCopyAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.copy(action.payload).pipe(
        map((data: any) => new KnBrDraftOrderCopySuccess(data)),
        catchError((error: Error) => {
          return of(new KnBrDraftOrderCopyFailure(error));
        })
      )
    )
  )
);

copySuccess$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCopySuccessAction),
    map((action) => {
      this.knBrCartContextService.set(action.payload.code);
      this.messageService.add({ key: 'cartCommon.copyMessage.success' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
      this.routeService.go({ cxRoute: 'cartDetails', params: { code: action.payload.code } });
    })
    ),
  { dispatch: false }
);

copyFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCopyFailureAction),
    map((action) => this.messageService.add({ key: 'cartCommon.copyMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR))
  ),
  { dispatch: false }
);

createOrder$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCreateAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.create(action.payload).pipe(
        map((data: any) => {
          data.orderToolsProducts = action.payload.orderToolsProducts;
          return new KnBrDraftOrderCreateSuccess(data);
        }),
        catchError((error: Error) => {
          return of(new KnBrDraftOrderCreateFailure(error));
        })
      )
    )
  )
);

createSuccess$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCreateSuccessAction),
    map((action) => {
      this.knBrCartContextService.set(action.payload.code);
      if (this.knBrCartContextService.pdpQuoteRef) {
        this.knBrQuoteReferenceService.pdpQuoteRef = _.cloneDeep(this.knBrCartContextService.pdpQuoteRef);
        this.knBrCartContextService.pdpQuoteRef = undefined;
      }

      this.multiCartService.loadCart({
        userId: null,
        cartId: action.payload.code,
        extraData: {
          active: true,
        },
      });
      const productList = this.activatedRoute.snapshot.queryParamMap.get('productList');
      const orderToolsProducts = action.payload.orderToolsProducts;
      if (productList || orderToolsProducts) {
        const parsedProducts = JSON.parse(productList);
        if (parsedProducts && parsedProducts.length > 0) {
          const parsedProductsCopy = { request: parsedProducts, cartId: action.payload.code };
          this.knBrDraftOrderService.quickEntry(parsedProductsCopy);
        } else if (orderToolsProducts && orderToolsProducts.length > 0) {
          const orderToolsReq = { request: orderToolsProducts, cartId: action.payload.code };
          this.knBrDraftOrderService.quickEntry(orderToolsReq);
        }
      } else {
        const productCode = this.activatedRoute.snapshot.queryParamMap.get('p');
        const quantity = this.activatedRoute.snapshot.queryParamMap.get('q');
        if (productCode) {
          this.multiCartService.addEntry(
            OCC_USER_ID_CURRENT,
            action.payload.code,
            productCode,
            quantity ? parseInt(quantity, 10) : 1
          );
        }
        //this.routeService.go({ cxRoute: 'cartDetails', params: { code: action.payload.code } });
      }

      //this.messageService.add({ key: 'cartCommon.createMessage.success' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
    })
  ),
  { dispatch: false }
);

createFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderCreateFailureAction),
    map((action) =>
      this.messageService.add({ key: 'cartCommon.createMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR)
    )),
    { dispatch: false }
);

updateOrder$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderUpdateAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.update(action.payload).pipe(
        map((data: any) => {
          data.isRedirect = action.payload.isRedirect;
          return new KnBrDraftOrderUpdateSuccess(data);
        }),
        catchError((error) => {
          return of(new KnBrDraftOrderUpdateFailure(tryNormalizeHttpError(error, this.logger)));
        })
      )
    )
  )
);

patchOrder$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderPatchAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.patch(action.payload).pipe(
        map((data: any) => {
          data.isRedirect = action.payload.isRedirect;
          return new KnBrDraftOrderUpdateSuccess(data);
        }),
        catchError((error) => {
          return of(new KnBrDraftOrderUpdateFailure(tryNormalizeHttpError(error, this.logger)));
        })
      )
    )
  )
);

updateSuccess$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderUpdateSuccessAction),
    map((action) => {
      this.knBrCartContextService.set(action.payload.code);
      if (action.payload.isRedirect) {
        this.routeService.goByUrl('carts');

        // To remove cart context if the cart is submitted for approval.
        if (action.payload.statusKey === 'CART_SELLER_SUBMITTED') {
          this.knBrCartContextService.delete();
        }
      } else {
        this.multiCartService.loadCart({
          userId: null,
          cartId: action.payload.code,
          extraData: {
            active: true,
          },
        });
      }
      this.messageService.add({ key: 'cartCommon.updateMessage.success' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
    })
  ),
  { dispatch: false }
);

  updateFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderUpdateFailureAction),
    map((action) => {
      const errors = (action.payload?.error?.errors || action.payload?.details || []) as Array<any>;
      if (errors.length > 0) {
        errors.forEach((error) => {
          const type = error.type.toLowerCase();
          if (type.indexOf('warning') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_WARNING);
          } else if (type.indexOf('error') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        });
      } else {
        this.messageService.add({ key: 'cartCommon.updateMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    })
  ), { dispatch: false }
);

updateEntry$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderEntryUpdateAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.updateEntry(action.payload).pipe(
        mergeMap((data: any) => {
          return [
            new KnBrDraftOrderEntryUpdateSuccess(data),
            new CartActions.LoadCart({ userId: null, cartId: action.payload.cartCode }),
          ];
        }),
        catchError((error: Error) => {
          return [new KnBrDraftOrderEntryUpdateFailure(tryNormalizeHttpError(error, this.logger)), new CartActions.ResetCartDetails()];
        })
      )
    )
  )
);

updateEntryFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderEntryUpdateFailureAction),
    map((action: any) => {
      const errors = (action.payload?.error?.errors || action.payload?.details || []) as Array<any>;
      if (errors.length > 0) {
        errors.forEach((error) => {
          const type = error.type.toLowerCase();
          if (type.indexOf('warning') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_WARNING);
          } else if (type.indexOf('error') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        });
      } else {
        this.messageService.add({ key: 'cartCommon.updateMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    })
  ), { dispatch: false }
);

chekoutValidationAction$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderChekoutValidationAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.validateOnCheckout().pipe(
        map((data: any) => {
          // return new CartActions.LoadCart({ userId: null, cartId: action.payload.cartCode });
          return new noop();
        }),
        catchError((error: Error) => {
         this.messageService.add({ key: 'cartCommon.updateMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR);
          return of(new CartActions.ResetCartDetails());
        })
      )
    )
  )
);

drfatOrderPriceadjust$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderPriceAdjustAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.draftOrderPriceadjust(action.payload).pipe(
        mergeMap((data: any) => {
          return [
            new KnBrDraftOrderPriceAdjustSuccess(data),
            new CartActions.LoadCart({ userId: null, cartId: action.payload.cartCode }),
          ];
        }),
        catchError((error: Error) => {
          return [new KnBrDraftOrderPriceAdjustFailure(tryNormalizeHttpError(error, this.logger)), new CartActions.ResetCartDetails()];
        })
      )
    )
  )
);

drfatOrderPriceadjustFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderPriceAdjustFailureAction),
    map((action: any) => {
      const errors = (action.payload?.error?.errors || action.payload?.details || []) as Array<any>;
      if (errors.length > 0) {
        errors.forEach((error) => {
          const type = error.type.toLowerCase();
          if (type.indexOf('warning') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_WARNING);
          } else if (type.indexOf('error') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        });
      } else {
        this.messageService.add({ key: 'cartCommon.updateMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    })
  ), { dispatch: false }
);

getStatuses$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderStatusesLoadAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.getCartStatuses().pipe(
        map((data: any) => new KnBrDraftOrderLoadStatusesSuccess(data)),
        catchError((error: Error) => {
          return of(new KnBrDraftOrderLoadStatusesFailure(error));
        })
      )
    )
  )
);

quickEntry$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderQuickEntryAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.quickEntry(action.payload).pipe(
        mergeMap((data: any) => {
          this.knBrDraftOrderService.quickFormReset(true);
          this.routeService.go({ cxRoute: 'cartDetails', params: { code: action.payload.cartId } });
          return [new KnBrDraftOrderQuickEntrySuccess(data)];
        }),
        catchError((error: Error) => {
          return of(new KnBrDraftOrderQuickEntryFailure(tryNormalizeHttpError(error, this.logger)));
        })
      )
    )
  )
);

quickEntrySuccess$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrdeQuickEntrySuccessAction),
    map((action) => {
      this.messageService.add({ key: 'addToCartMessage.success' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
      const addedEntries = action.payload.knbrCartModificationList;
      if (addedEntries.length > 0) {
        if (addedEntries && addedEntries[0].statusCode === 'success' && addedEntries[0].statusMessage) {
          this.messageService.add({ raw: addedEntries[0].statusMessage }, GlobalMessageType.MSG_TYPE_WARNING);
        }
      }
    })
  ), { dispatch: false }
);

quickEntryFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderQuickEntryFailureAction),
    map((action) => {
      const errors = (action.payload?.error?.errors || action.payload?.details || []) as Array<any>;
      if (errors.length > 0) {
        errors.forEach((error) => {
          const type = error.type.toLowerCase();
          if (type.indexOf('warning') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_WARNING);
          } else if (type.indexOf('error') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        });
      } else {
        this.messageService.add({ key: 'addToCartMessage.failure' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    })
  ), { dispatch: false }
);

deleteOrder$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderDeleteAction),
    mergeMap((action) =>
      this.draftOrdersAdapter.delete(action.payload).pipe(
        map((data: any) => {
          // data.isRedirect = action.payload.isRedirect;
          return new KnBrDraftOrderDeleteSuccess(data);
        }),
        catchError((error) => {
          return of(new KnBrDraftOrderDeleteFailure(tryNormalizeHttpError(error,this.logger)));
        })
      )
    )
  )
);

deleteSuccess$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderDeleteSuccessAction),
    map((action) => {
      this.routeService.goByUrl('carts');
      this.knBrCartContextService.delete();
      this.messageService.add({ key: 'cartCommon.deleteMessage.success' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
    })
  ), { dispatch: false }
);

deleteFailure$ = createEffect(() =>
  this.actions$.pipe(
    ofType(KnBrDraftOrdersActionTypes.KnBrDraftOrderDeleteFailureAction),
    map((action) => {
      const errors = (action.payload?.error?.errors || action.payload?.details || []) as Array<any>;
      if (errors.length > 0) {
        errors.forEach((error) => {
          const type = error.type.toLowerCase();
          if (type.indexOf('warning') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_WARNING);
          } else if (type.indexOf('error') !== -1) {
            this.messageService.add({ raw: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
          }
        });
      } else {
        this.messageService.add({ key: 'cartCommon.deleteMessage.error' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    })
  ), { dispatch: false }
);


  constructor(
    private actions$: Actions<KnBrDraftOrdersSearchActions>,
    private draftOrdersAdapter: KnBrDraftOrdersAdapter,
    private messageService: GlobalMessageService,
    private routeService: RoutingService,
    private activatedRoute: ActivatedRoute,
    private multiCartService: MultiCartService,
    private knBrCartContextService: KnBrCartContextService,
    private knBrQuoteReferenceService: KnBrQuoteReferenceService,
    private knBrDraftOrderService: KnBrDraftOrdersService,
    private logger: LoggerService
  ) {}
}
